import { onBeforeUnmount, onMounted, watch, type WatchStopHandle } from 'vue'
import { useFlag } from '@/utils/featureFlagStore'

import {
  getActiveIframeSession,
  IFRAME_READY_MESSAGE,
  isIframeSessionHydrationMessage,
  isSafeParentOrigin,
  setIframeSession,
} from '@/auth/iframeSession'
import { getAuthorizer } from '@/auth/useAuthorizer'
import { useAuthStore } from '@/auth/useAuthStore'
import { logError } from '@/logger'

export function useIframeCookieRenewal(): void {
  const enabled = useFlag('hermes.hestiaCookieBasedLogin')
  const authStore = useAuthStore()
  let epoch = 0
  let stopWatching: WatchStopHandle | undefined
  const initialSession = getActiveIframeSession()
  const parentOrigin = initialSession?.parentOrigin
  let disposed = false
  let renewal = Promise.resolve()
  const handleMessage = (event: MessageEvent): void => {
    if (
      !enabled.value ||
      !initialSession?.csrfToken ||
      !parentOrigin ||
      !isSafeParentOrigin(parentOrigin) ||
      event.origin !== parentOrigin ||
      event.source !== window.parent ||
      window.location.pathname === '/iframe-auth' ||
      !isIframeSessionHydrationMessage(event.data) ||
      !event.data.session.csrfToken
    )
      return
    const { session } = event.data
    const currentEpoch = epoch
    renewal = renewal.then(async () => {
      if (disposed || !enabled.value || currentEpoch !== epoch) return
      if (
        session.user.id !== authStore.user?.id ||
        session.brandId !== authStore.brandSession?.brandId
      )
        return
      try {
        setIframeSession({
          ...initialSession,
          apiUrl: session.apiUrl,
          audience: session.audience,
          csrfToken: session.csrfToken,
          expiresAt: Date.parse(session.expiresAt),
        })
        authStore.setEmbeddedSession({
          csrfToken: session.csrfToken,
          expiresAt: session.expiresAt,
          user: session.user,
        })
        await authStore.reinitializeAuthorizer('iframe-cookie-renewal')
        if (!getAuthorizer())
          throw new Error('Authorization unavailable after renewal')
        if (!disposed && enabled.value && currentEpoch === epoch)
          window.parent.postMessage(
            { type: 'HERMES_SESSION_HYDRATION_SUCCESS' },
            parentOrigin
          )
      } catch (error) {
        logError(error, { context: 'iframe-cookie-renewal' })
        if (!disposed && enabled.value && currentEpoch === epoch)
          window.parent.postMessage(
            { type: 'HERMES_SESSION_HYDRATION_ERROR' },
            parentOrigin
          )
      }
    })
  }
  onMounted(() => {
    stopWatching = watch(
      enabled,
      (active) => {
        epoch += 1
        window.removeEventListener('message', handleMessage)
        if (!active) return
        window.addEventListener('message', handleMessage)
        if (
          initialSession?.csrfToken &&
          parentOrigin &&
          isSafeParentOrigin(parentOrigin) &&
          window.location.pathname !== '/iframe-auth'
        ) {
          window.parent.postMessage(IFRAME_READY_MESSAGE, parentOrigin)
        }
      },
      { flush: 'sync', immediate: true }
    )
  })
  onBeforeUnmount(() => {
    disposed = true
    stopWatching?.()
    window.removeEventListener('message', handleMessage)
  })
}
